import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { supabaseCoreClient } from '../utils/supabase.js';
import { queryPolicies } from '../utils/queryPolicies.js';
import type { GridColumnDefault, GridColumnDefaultEntry } from '../utils/gridColumnDefaults.js';

/**
 * REG-828: admin default columns (core_crm.grid_column_default), every grid's
 * in one small cached read shared by all grids. Live changes on the table
 * invalidate it (queryKeyRegistry), so an admin's change reaches open tabs.
 * Only manage_default_layouts holders (the Dev group; super admins pass) can write; RLS enforces that.
 */
export const GRID_COLUMN_DEFAULTS_QUERY_KEY = ['grid-column-defaults'] as const;

type GridColumnDefaultRecords = Record<string, GridColumnDefault>;

export async function loadGridColumnDefaults(): Promise<GridColumnDefaultRecords> {
  const { data, error } = await (supabaseCoreClient as any)
    .from('grid_column_default')
    .select('grid_key,columns,version,updated_at,updated_by');
  if (error) throw error;
  const records: GridColumnDefaultRecords = {};
  for (const row of (data ?? []) as Array<{ grid_key: string; columns: GridColumnDefaultEntry[]; version: number | string; updated_at: string | null; updated_by: number | null }>) {
    if (!Array.isArray(row.columns)) continue;
    records[row.grid_key] = {
      gridKey: row.grid_key,
      columns: row.columns,
      version: Number(row.version) || 0,
      updatedAt: row.updated_at,
      updatedBy: row.updated_by,
    };
  }
  return records;
}

export async function saveGridColumnDefault(gridKey: string, columns: GridColumnDefaultEntry[]): Promise<void> {
  const { error } = await (supabaseCoreClient as any)
    .from('grid_column_default')
    .upsert({ grid_key: gridKey, columns }, { onConflict: 'grid_key' });
  if (error) throw error;
}

export function useGridColumnDefaults() {
  return useQuery({
    queryKey: GRID_COLUMN_DEFAULTS_QUERY_KEY,
    queryFn: loadGridColumnDefaults,
    retry: 1,
    ...queryPolicies.configuration,
  });
}

/**
 * The admin default for one grid. `loaded` turns true once the defaults have
 * been read, or the read failed: a grid then shows the page's own defaults
 * rather than waiting or breaking.
 */
export function useGridColumnDefault(gridKey: string | undefined) {
  const queryClient = useQueryClient();
  const query = useGridColumnDefaults();
  useEffect(() => {
    if (query.error) console.error('Failed to load the default grid columns:', query.error);
  }, [query.error]);
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: GRID_COLUMN_DEFAULTS_QUERY_KEY }),
    [queryClient],
  );
  return {
    loaded: !gridKey || !query.isPending,
    gridDefault: gridKey ? query.data?.[gridKey] ?? null : null,
    refresh,
  };
}
